import { useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  Box,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import {
  useRetailerQuery,
  useCreateRetailerMutation,
  useUpdateRetailerMutation,
} from '../hooks/useRetailersQuery';
import type { CreateRetailerDTO } from '../types/retailers.types';

interface RetailerFormDialogProps {
  open: boolean;
  onClose: () => void;
  retailerId: string | null;
}

export default function RetailerFormDialog({
  open,
  onClose,
  retailerId,
}: RetailerFormDialogProps) {
  const isEdit = !!retailerId;
  const { data: retailer } = useRetailerQuery(retailerId);
  const createMutation = useCreateRetailerMutation();
  const updateMutation = useUpdateRetailerMutation();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateRetailerDTO>({
    defaultValues: {
      country: '',
      retailerId: '',
      retailerName: '',
      isActive: true,
    },
  });

  useEffect(() => {
    if (retailer && isEdit) {
      reset({
        country: retailer.country,
        retailerId: retailer.retailerId,
        retailerName: retailer.retailerName,
        isActive: retailer.isActive,
      });
    } else if (!isEdit) {
      reset({
        country: '',
        retailerId: '',
        retailerName: '',
        isActive: true,
      });
    }
  }, [retailer, isEdit, reset]);

  const onSubmit = async (data: CreateRetailerDTO) => {
    try {
      if (isEdit && retailerId) {
        await updateMutation.mutateAsync({
          retailerId,
          data: {
            country: data.country,
            retailerName: data.retailerName,
            isActive: data.isActive,
          },
        });
      } else {
        await createMutation.mutateAsync(data);
      }
      onClose();
    } catch (err) {
      console.error('Error saving retailer:', err);
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Editar Retailer' : 'Nuevo Retailer'}</DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Controller
              name="country"
              control={control}
              rules={{ required: 'El país es requerido' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="País"
                  error={!!errors.country}
                  helperText={errors.country?.message}
                  fullWidth
                />
              )}
            />
            <Controller
              name="retailerId"
              control={control}
              rules={{ required: 'El ID es requerido' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="ID Retailer"
                  error={!!errors.retailerId}
                  helperText={errors.retailerId?.message}
                  fullWidth
                  disabled={isEdit}
                />
              )}
            />
            <Controller
              name="retailerName"
              control={control}
              rules={{ required: 'El nombre es requerido' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Nombre"
                  error={!!errors.retailerName}
                  helperText={errors.retailerName?.message}
                  fullWidth
                />
              )}
            />
            <Controller
              name="isActive"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                    />
                  }
                  label="Activo"
                />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isLoading}>
            Cancelar
          </Button>
          <Button type="submit" variant="contained" disabled={isLoading}>
            {isLoading ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
